<script lang="ts" setup>
import { capitalize } from '@sancare/typescript-commons/src/misc/string-utils'
import { computed, ComputedRef } from 'vue'

import { CursorHelper, getWeekNbText } from '@/stay-displayer/smr/utils'
import { DependenciesLabels, DependenciesSections, Rhs } from '@/store/modules/stay/smr/types'

const props = defineProps<{
  rhsList: Rhs[]
  hideCmGnRow: boolean
}>()

const rhsNavigator = new CursorHelper<Rhs>(props.rhsList, 14)

function getCells(values: (string | number)[], empty: string = null): CellData[] {
  let lastVal = null
  return values.map((currentVal) => {
    if (!currentVal) {
      return {
        value: empty,
        class: 'empty',
      }
    }
    const result = {
      value: currentVal,
      class: lastVal !== null && currentVal !== lastVal ? 'deviation' : null
    }
    lastVal = currentVal
    return result
  })
}

function getActsCells(cursor: CursorHelper<Rhs>, getNbActs: (rhs: Rhs) => number): CellData[] {
  return cursor.displayedElements.value.map((rhs) => {
    const nbActs = getNbActs(rhs)
    return {
      value: nbActs,
      class: nbActs === 0 ? 'deviation' : ''
    }
  })
}

type CellData = {
  value: string | number
  class: string
}
type RowData = {
  label: string
  class: string
  cells: CellData[]
}

function sumDep(rhs: Rhs, section: DependenciesSections): number {
  let sum = 0
  for (const dep of Object.keys(DependenciesLabels[section])) {
    const prop = `current${capitalize(dep)}`
    sum += rhs[prop] ?? 0
  }
  return sum
}

const countDisplayedActs = <T extends { disabled: boolean }>(acts: T[]): number => {
  return acts.reduce((count: number, act: T) => count + (act.disabled ? 0 : 1), 0)
}

const gmeList = computed(() => rhsNavigator.displayedElements.value.map((rhs) => rhs.currentModeDiagnosisGroup.groupingResult.gme?.reference ?? null))
const cmGnList = computed(() => props.hideCmGnRow ? [] : getCells(gmeList.value.map((gme) => gme?.substring(0, 4), '-')))
const physicalList = computed(() => getCells(rhsNavigator.displayedElements.value.map((rhs) => sumDep(rhs, DependenciesSections.PHYSICAL))))
const cognitiveList = computed(() => getCells(rhsNavigator.displayedElements.value.map((rhs) => sumDep(rhs, DependenciesSections.COGNITIVE))))
const nbMedicalActs = computed(() => getActsCells(rhsNavigator, (rhs: Rhs): number => countDisplayedActs(rhs.codedMedicalActs)))
const nbRehabilitationActs = computed(() => getActsCells(rhsNavigator, (rhs: Rhs): number => countDisplayedActs(rhs.codedRehabilitationActs)))
const tableData = computed(() => {
  return [
    {
      label: 'CM GN',
      class: 'nosology',
      cells: cmGnList.value,
    },
    {
      label: 'Physique',
      class: 'weight',
      cells: physicalList.value,
    },
    {
      label: 'Cognitive',
      class: 'weight',
      cells: cognitiveList.value,
    },
    {
      label: 'Actes CCAM',
      class: 'act',
      cells: nbMedicalActs.value,
    },
    {
      label: 'Actes CSAR',
      class: 'act',
      cells: nbRehabilitationActs.value,
    },
  ].filter((row) => {
    if (props.hideCmGnRow && row.label === 'CM GN') {
      return false
    }

    return true
  })
}) as ComputedRef<RowData[]>
</script>

<template>
  <div class="row my-5">
    <div class="col-auto p-0 pt-5">
      <span
        class="display-5"
        :class="{cp: rhsNavigator.canMoveDown.value}"
        :style="{opacity: !rhsNavigator.canMove.value ? 0 : rhsNavigator.canMoveDown.value ? 1 : 0.3}"
        @click.prevent="rhsNavigator.moveCursor(-4)"
      >&#8592;</span>
    </div>
    <div class="col">
      <table
        id="rhs-table"
        :class="rhsNavigator.canMove.value ? 'w-100' : null"
      >
        <thead>
          <tr>
            <th />
            <th
              v-for="rhs in rhsNavigator.displayedElements.value"
              :key="rhs.id"
              class="fw-normal"
            >
              <span v-tooltip="rhs.startDate.format('DD/MM/YYYY')">{{ getWeekNbText(rhs, true, true) }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in tableData"
            :key="row.label"
          >
            <td
              class="text-end"
              :class="null === row.class ? null : 'color-'+row.class"
            >
              {{ row.label }}
            </td>
            <td
              v-for="(cell, idx) in row.cells"
              :key="idx"
            >
              <div :class="cell?.class ?? null">
                {{ cell?.value ?? '-' }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="col-auto p-0 pt-5">
      <span
        :style="{opacity: !rhsNavigator.canMove.value ? 0 : (rhsNavigator.canMoveUp.value ? 1 : 0.3)}"
        class="display-5"
        :class="{cp: rhsNavigator.canMoveUp.value}"
        @click.prevent="rhsNavigator.moveCursor(4)"
      >&#8594;</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@sancare/typescript-commons/src/assets/styles/variables';
@import '@/assets/styles/variables';

.display-5 {
  user-select: none;
}

#rhs-table {
  table-layout: fixed;
  max-width: 100%;

  td,
  th {
    text-align: center;
    padding: 4px 6px;

    &:first-of-type {
      text-align: left;
    }
  }

  td {

    &.color-act {
      color: #3d9542;
    }

    div {
      border-radius: 10px;
      margin: auto;
      padding: 5px 10px;
      border: 2px solid $sancare-light-grey;
      background-color: white;

      &.deviation {
        border: 2px solid $sancare-rhs-medium-yellow;
        background-color: $sancare-rhs-medium-yellow;
      }

      &.empty {
        background-color: $sancare-light-grey;
      }
    }
  }
}
</style>
